import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Bookmark, Plus, X } from 'lucide-react';

export const SavedViews = ({ page, getConfig, applyConfig }) => {
  const api = useApi();
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/table-views?page=${page}`).then(r => setViews(r.data)).catch(() => {});
  }, [api, page]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/table-views', { page, name: name.trim(), config: getConfig() });
      toast.success(`View "${data.name}" saved`);
      setSaving(false); setName(''); setActiveId(data.view_id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save view'); }
    setBusy(false);
  };

  const remove = async (v, ev) => {
    ev.stopPropagation();
    try {
      await api.delete(`/table-views/${v.view_id}`);
      if (activeId === v.view_id) setActiveId(null);
      setViews(prev => prev.filter(x => x.view_id !== v.view_id));
      toast.success(`View "${v.name}" deleted`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete view'); }
  };

  if (views.length === 0 && !saving) {
    return (
      <button onClick={() => setSaving(true)} data-testid="save-view-btn"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[11px] font-medium transition-colors hover:bg-[rgba(26,76,124,0.06)]"
        style={{ color: '#5C6B7A', border: '1px dashed rgba(136,150,168,0.4)' }}
        title="Save the current search, filters and sort as a one-click view">
        <Bookmark className="w-3 h-3" />Save view
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`saved-views-${page}`}>
      <Bookmark className="w-3.5 h-3.5 shrink-0" style={{ color: '#5C6B7A' }} />
      {views.map(v => (
        <button key={v.view_id} onClick={() => { applyConfig(v.config); setActiveId(v.view_id); }}
          data-testid={`saved-view-${v.view_id}`}
          className="group flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-medium transition-colors"
          style={activeId === v.view_id
            ? { background: '#0D1B2A', color: 'white' }
            : { background: 'rgba(26,76,124,0.07)', color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.2)' }}>
          {v.name}
          <span onClick={(ev) => remove(v, ev)} data-testid={`delete-view-${v.view_id}`}
            className="rounded-full p-0.5 opacity-40 group-hover:opacity-100 hover:bg-white/20 transition-opacity"
            title="Delete view"><X className="w-2.5 h-2.5" /></span>
        </button>
      ))}
      {saving ? (
        <span className="flex items-center gap-1.5">
          <input autoFocus value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) save(); if (e.key === 'Escape') setSaving(false); }}
            placeholder="View name…" maxLength={40}
            className="px-2 py-1 text-[11px] rounded-sm border w-36 focus:outline-none focus:ring-1 focus:ring-[#1A4C7C]"
            style={{ borderColor: 'rgba(136,150,168,0.35)', color: '#0D1B2A' }} data-testid="view-name-input" />
          <button onClick={save} disabled={busy || !name.trim()} data-testid="view-save-confirm"
            className="px-2.5 py-1 rounded-sm text-[11px] font-semibold text-white disabled:opacity-40" style={{ background: '#1A4C7C' }}>Save</button>
          <button onClick={() => { setSaving(false); setName(''); }} className="text-[11px]" style={{ color: '#5C6B7A' }}>✕</button>
        </span>
      ) : (
        <button onClick={() => setSaving(true)} data-testid="save-view-btn"
          className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors hover:bg-[rgba(26,76,124,0.06)]"
          style={{ color: '#5C6B7A', border: '1px dashed rgba(136,150,168,0.4)' }}>
          <Plus className="w-3 h-3" />Save view
        </button>
      )}
    </div>
  );
};

export default SavedViews;
